import { useEffect, useState } from "react";

import { useResolvedTheme } from "@/lib/useResolvedTheme";

type Highlighter = {
  codeToHtml: (code: string, opts: { lang: string; theme: string }) => string;
  getLoadedLanguages: () => string[];
};

const LANGS = [
  "python",
  "typescript",
  "javascript",
  "tsx",
  "jsx",
  "bash",
  "shell",
  "sql",
  "json",
  "yaml",
  "markdown",
  "html",
  "css",
  "rust",
  "go",
  "java",
  "csharp",
  "cpp",
  "xml",
  "diff",
];

let highlighterPromise: Promise<Highlighter> | null = null;

function getHighlighter(): Promise<Highlighter> {
  if (!highlighterPromise) {
    highlighterPromise = import("shiki").then(async ({ createHighlighter }) => {
      const h = await createHighlighter({
        themes: ["github-light", "github-dark"],
        langs: LANGS,
      });
      return h as unknown as Highlighter;
    });
  }
  return highlighterPromise;
}

function normalizeLang(raw: string | undefined): string {
  if (!raw) return "text";
  const l = raw.toLowerCase().replace(/^language-/, "").trim();
  if (l === "" || l === "text" || l === "plain" || l === "plaintext") return "text";
  if (l === "ts") return "typescript";
  if (l === "js") return "javascript";
  if (l === "sh" || l === "zsh") return "bash";
  if (l === "yml") return "yaml";
  if (l === "py") return "python";
  if (l === "cs") return "csharp";
  if (l === "c++") return "cpp";
  if (l === "md") return "markdown";
  return l;
}

export function SyntaxCodeBlock({
  code,
  language,
}: {
  code: string;
  language?: string;
}) {
  const lang = normalizeLang(language);
  const resolvedTheme = useResolvedTheme();
  const shikiTheme = resolvedTheme === "dark" ? "github-dark" : "github-light";
  const [html, setHtml] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (lang === "text") {
      setHtml(null);
      return;
    }
    (async () => {
      try {
        const highlighter = await getHighlighter();
        if (cancelled) return;
        const supported = highlighter.getLoadedLanguages();
        const effective = supported.includes(lang) ? lang : "text";
        if (effective === "text") {
          setHtml(null);
          return;
        }
        const result = highlighter.codeToHtml(code, {
          lang: effective,
          theme: shikiTheme,
        });
        if (!cancelled) setHtml(result);
      } catch {
        if (!cancelled) setHtml(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, lang, shikiTheme]);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard write may fail in insecure contexts; user can copy manually */
    }
  }

  return (
    <div className="group relative my-[var(--space-5)] overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-sunken)] shadow-[var(--elev-ring)]">
      <div className="flex items-center justify-between border-b border-[var(--border-default)] px-[var(--space-4)] py-[var(--space-2)]">
        <span className="type-overline text-[var(--text-tertiary)]">
          {lang === "text" ? "code" : lang}
        </span>
        <button
          type="button"
          onClick={onCopy}
          aria-label={copied ? "Copied" : "Copy code"}
          className="type-caption rounded-[var(--radius-pill)] px-[var(--space-2)] py-[var(--space-1)] text-[var(--text-tertiary)] opacity-0 transition-[opacity,background-color,color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-raised)] hover:text-[var(--text-secondary)] focus-visible:opacity-100 focus-visible:shadow-[var(--elev-focus)] focus-visible:outline-none group-hover:opacity-100 [@media(hover:none)]:opacity-100"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {html ? (
        <div
          className="type-code overflow-x-auto p-[var(--space-4)] text-[var(--text-primary)] [&_pre]:!bg-transparent [&_pre]:!p-0"
          // Shiki output is sanitized; only color-classed <span> tags. No script vectors.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <pre className="type-code overflow-x-auto p-[var(--space-4)] text-[var(--text-primary)]">
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
}
